import React, { useState, useEffect, forwardRef, useImperativeHandle,useRef } from 'react';
import '../styles/Snackbar.scss'
import '../styles/snackypop.scss'

interface SnackbarProps {
    message: string;
    duration?: number; // Duration to display the snackbar in milliseconds (default 3000ms)
    position?: 'top' | 'bottom' | 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'; // Position of the snackbar
    actions?: React.ReactNode; // Custom actions to display inside the snackbar
    //ref?: SnackBarRef;
}

export interface SnackBarRef {
    shakeIt: () => void;
}

const Snackbar = forwardRef<SnackBarRef, SnackbarProps>(({
    message,
    duration = -1,
    position = 'bottom-left',
    actions,
}, ref) => {

    const [isVisible, setIsVisible] = useState(false);
    const snackbarContentRef = useRef<HTMLDivElement>(null);

    // Expose the shakeIt method to the parent
    useImperativeHandle(ref, () => ({
        shakeIt: () => {
            if (snackbarContentRef.current) {
                snackbarContentRef.current.classList.add("shake");
                setTimeout(() => {
                    snackbarContentRef.current?.classList.remove("shake");
                }, 300);
            }
        },
    }));

    useEffect(() => {
        if (message) {
            setIsVisible(true);

            let timer: NodeJS.Timeout | null = null;
            if (duration > -1) {
                timer = setTimeout(() => {
                    setIsVisible(false);
                }, duration);
            }

            // Cleanup the timer when the component unmounts or the message changes
            return () => {
                if (timer) clearTimeout(timer);
            };
        }
    }, [message, duration]);

    // Return null instead of an empty string when there's no message
    if (!message) {
        return null;
    }

    return (
        <div
            className={`snackbar ${isVisible ? 'show' : ''} ${position}`}
            role="alert"
        >
            <div className="snackbar-content" ref={snackbarContentRef}>
                {message}
                {actions && <div className="snackbar-actions">{actions}</div>}
            </div>
        </div>
    );
});

export default Snackbar;



export interface SnackyPopProps {
    message: string;
    header?: string;
    type?: "snackybar" | "popup";
    position?: 'top' | 'bottom' | 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    overlay?: boolean;
    closeIcon?: boolean;
    duration?: number;
    actions?: SnackyPopAction[];
}

export interface SnackyPopAction {
    name: string;
    type: "button" | "label" | "icon";
    text?: string;
    icon?: string;
    onAction: (action: SnackyPopAction) => Promise<void>;
}

export class SnackyPop {

    static showSnack(props: SnackyPopProps) {
        props.type = "snackybar";
        return SnackyPop.show(props);
    }
    static show(props: SnackyPopProps) {
        props.position ??= 'top';
        props.type ??= "popup";
        props.header ??= "";

        // if the type is snackybar and duration is not given, set it to 2 sec max.
        if (props.type == 'snackybar' && !props.duration) props.duration = 2000;

        let id = "snackypop_" + (Number(Math.random().toFixed(3)) * 1000).toString();
        props.actions ??= [];
        let popup = `
            <div role="alert" class="snackypop-content ${props.type} ${props.position}">   
                <div class="snackypop-header ${props.header == '' ? 'd-none' : ''}"></div>
                <div class="snackypop-body">
                    <p>${props.message}</p>      
                </div>
                <div class="snackypop-actions ${props.actions.length == 0 ? 'd-none' : ''}">
                    ${props.actions.map(action => {
            if (action.type === 'button')
                return `<button name='${action.name}' class='snackypop-actionelem snackypop-btn'>${action.text}</button>`;
            if (action.type === 'label')
                return `<label name='${action.name}' class='snackypop-actionelem snackypop-label'>${action.text}</label>`;
            if (action.type === 'icon')
                return `<span name='${action.name}' class='snackypop-actionelem snackypop-icon material-symbols-outlined'>${action.icon}</span>`;
        }).join("")
            }
                </div>
            </div>`;

        let div = document.createElement("div");
        div.id = id;
        div.classList.add("snackypop-container");
        div.classList.add(props.type);
        div.classList.add(props.position);
        props.overlay && div.classList.add("snackypop-overlay");

        div.innerHTML = popup;

        document.body.appendChild(div);

        let elem = document.querySelector(`#${id} .snackypop-actions`) as HTMLElement;

        elem.addEventListener("click", (ev) => {
            let action = props.actions?.find(x => x.name === (ev.target as HTMLElement).getAttribute("name"));
            action?.onAction(action);
        });

        if (props.duration && props.duration > -1) {
            setTimeout(() => {
                this.close(id);
            }, props.duration);
        }

        return id;
    }

    static close(id: string) {
        let popup = document.getElementById(id);
        if (!popup) return;
        popup.querySelector(".snackypop-content")?.classList.add("close");
        setTimeout(() => {
            popup && document.body.removeChild(popup);
        }, 300);
    }

    static closeAll() {
        let popups = document.querySelectorAll(".snackypop-container") || [];
        popups.forEach(popup => document.body.removeChild(popup));
    }

}